import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showAppAlert } from "../../../store/reducers/appreducer";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Paper,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Update as UpdateIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Mic as MicIcon,
  Style as StyleIcon,
  Schedule as ScheduleIcon,
  Chat as ChatIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
} from "@mui/icons-material";
import {
  InterviewConfig,
  GetInterviewConfig,
  UpdateInterviewConfig,
} from "../../../services/interviewService";

const InterviewConfigPage = () => {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [config, setConfig] = useState({
    interview_voice_type: "",
    interview_style: "",
    interview_duration: "",
    interview_interaction_mode: "",
    voice_id: "",
  });

  // Form validation
  useEffect(() => {
    const isValid = Object.values(config).every((value) => value !== "");
    setIsFormValid(isValid);
  }, [config]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!jobId) {
        setInitialLoading(false);
        return;
      }

      try {
        setInitialLoading(true);
        setError(null);
        const data = await GetInterviewConfig(jobId);
        if (data && Object.keys(data).length > 0) {
          setConfig(data);
          setHasExistingConfig(true);
        }
        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage: "Interview configuration loaded successfully",
          })
        );
      } catch (err) {
        setError("Failed to load interview configuration");
        dispatch(
          showAppAlert({
            show: true,
            alertType: "warning",
            alertMessage:
              err.response?.data?.message || "Failed to load configuration",
          })
        );
        console.error("Error loading config:", err);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchConfig();
  }, [jobId, dispatch]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setConfig((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!jobId) {
      setError("Job ID is required");
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: "Job ID is required to save configuration",
        })
      );
      return;
    }

    if (!isFormValid) {
      setError("Please fill in all required fields");
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: "Please complete all configuration fields",
        })
      );
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (hasExistingConfig) {
        await UpdateInterviewConfig(config, jobId);
        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage: "Interview configuration updated successfully",
          })
        );
      } else {
        await InterviewConfig(config, jobId);
        setHasExistingConfig(true);
        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage: "Interview configuration saved successfully",
          })
        );
      }
    } catch (err) {
      setError("Failed to save interview configuration");
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage:
            err.response?.data?.message || "Failed to save configuration",
        })
      );
      console.error("Error saving config:", err);
    } finally {
      setLoading(false);
    }
  };

  const getFieldIcon = (fieldName) => {
    const icons = {
      interview_voice_type: <MicIcon />,
      interview_style: <StyleIcon />,
      interview_duration: <ScheduleIcon />,
      interview_interaction_mode: <ChatIcon />,
    };
    return icons[fieldName] || <InfoIcon />;
  };

  const getFieldDescription = (fieldName) => {
    const descriptions = {
      interview_voice_type: "Choose the voice type for the AI interviewer",
      interview_style: "Select the tone and approach for the interview",
      interview_duration: "Set the expected length of the interview",
      interview_interaction_mode:
        "Define how the AI should interact with candidates",
      voice_id: "Select the specific voice variant for the AI interviewer",
    };
    return descriptions[fieldName] || "";
  };

  if (!jobId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="warning"
          icon={<InfoIcon />}
          sx={{
            borderRadius: 2,
            "& .MuiAlert-message": { fontWeight: 500 },
          }}
        >
          Please select a job to configure interview settings
        </Alert>
      </Box>
    );
  }

  if (initialLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          gap: 2,
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading interview configuration...
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={!initialLoading} timeout={500}>
      <Box sx={{ p: 2 }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "16px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              // color: "white",
              p: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <SettingsIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 700,
                  mb: 0.5,
                }}
              >
                Interview Configuration
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Customize your AI interviewer settings for optimal candidate
                experience
              </Typography>
            </Box>
            {hasExistingConfig && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Configured"
                color="success"
                sx={{ ml: "auto" }}
              />
            )}
          </Box>

          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={4}>
                {/* Voice Type */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #E8E8E8",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#667eea",
                        boxShadow: "0px 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <MicIcon sx={{ color: "#667eea", mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Voice Type
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {getFieldDescription("interview_voice_type")}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Voice Type</InputLabel>
                        <Select
                          name="interview_voice_type"
                          value={config.interview_voice_type}
                          label="Select Voice Type"
                          onChange={handleChange}
                          disabled={loading}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="male">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <MaleIcon sx={{ color: "#667eea" }} />
                              Male Voice
                            </Box>
                          </MenuItem>
                          <MenuItem value="female">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <FemaleIcon sx={{ color: "#667eea" }} />
                              Female Voice
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Voice Id */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #E8E8E8",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#667eea",
                        boxShadow: "0px 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <MicIcon sx={{ color: "#667eea", mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Voice Id
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {getFieldDescription("voice_id")}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Voice Id</InputLabel>
                        <Select
                          name="voice_id"
                          value={config.voice_id}
                          label="Select Voice Id"
                          onChange={handleChange}
                          disabled={loading}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="1">Voice 1</MenuItem>
                          <MenuItem value="2">Voice 2</MenuItem>
                          <MenuItem value="3">Voice 3</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Interview Style */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #E8E8E8",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#667eea",
                        boxShadow: "0px 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <StyleIcon sx={{ color: "#667eea", mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Interview Style
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {getFieldDescription("interview_style")}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Interview Style</InputLabel>
                        <Select
                          name="interview_style"
                          value={config.interview_style}
                          label="Select Interview Style"
                          onChange={handleChange}
                          disabled={loading}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="formal">
                            Formal & Professional
                          </MenuItem>
                          <MenuItem value="friendly">
                            Friendly & Approachable
                          </MenuItem>
                          <MenuItem value="casual">Casual & Relaxed</MenuItem>
                          <MenuItem value="empathetic">
                            Empathetic & Understanding
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Duration */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #E8E8E8",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#667eea",
                        boxShadow: "0px 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <ScheduleIcon sx={{ color: "#667eea", mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Duration
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {getFieldDescription("interview_duration")}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Duration</InputLabel>
                        <Select
                          name="interview_duration"
                          value={config.interview_duration}
                          label="Select Duration"
                          onChange={handleChange}
                          disabled={loading}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="short">
                            Short (15-20 minutes)
                          </MenuItem>
                          <MenuItem value="medium">
                            Medium (25-35 minutes)
                          </MenuItem>
                          <MenuItem value="long">Long (40-50 minutes)</MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Interaction Mode */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      border: "1px solid #E8E8E8",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "#667eea",
                        boxShadow: "0px 4px 12px rgba(102, 126, 234, 0.15)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <ChatIcon sx={{ color: "#667eea", mr: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Interaction Mode
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {getFieldDescription("interview_interaction_mode")}
                      </Typography>
                      <FormControl fullWidth>
                        <InputLabel>Select Interaction Mode</InputLabel>
                        <Select
                          name="interview_interaction_mode"
                          value={config.interview_interaction_mode}
                          label="Select Interaction Mode"
                          onChange={handleChange}
                          disabled={loading}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#E0E0E0",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#667eea",
                            },
                          }}
                        >
                          <MenuItem value="open-ended">
                            Open-ended Questions
                          </MenuItem>
                          <MenuItem value="follow-up-driven">
                            Follow-up Driven
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 3 }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
                  >
                    <Tooltip
                      title={!isFormValid ? "Please complete all fields" : ""}
                    >
                      <span>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading || !isFormValid}
                          startIcon={
                            loading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : hasExistingConfig ? (
                              <UpdateIcon />
                            ) : (
                              <SaveIcon />
                            )
                          }
                          sx={{
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                            borderRadius: "12px",
                            px: 4,
                            py: 1.5,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "16px",
                            boxShadow: "0px 4px 12px rgba(102, 126, 234, 0.3)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                              boxShadow:
                                "0px 6px 16px rgba(102, 126, 234, 0.4)",
                            },
                            "&:disabled": {
                              background: "#E0E0E0",
                              color: "#9E9E9E",
                            },
                          }}
                        >
                          {loading
                            ? hasExistingConfig
                              ? "Updating..."
                              : "Saving..."
                            : hasExistingConfig
                            ? "Update Configuration"
                            : "Save Configuration"}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Paper>
      </Box>
    </Fade>
  );
};

export default InterviewConfigPage;
